import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Cloud } from 'lucide-react';
import { useAppContext } from '../../context/AppContext';
import { useAuth } from '../../context/AuthContext';
import apiService from '../../services/api';
import UpstreamDataModal from './UpstreamDataModal';

const ProgramSummary = ({ programId, programData: programOverview }) => {
  const { t } = useAppContext();
  const { hasPermission } = useAuth();
  const [relatedData, setRelatedData] = useState(null);
  const navigate = useNavigate();

  const [error, setError] = useState(null);

  // Upstream data modal state
  const [showUpstreamModal, setShowUpstreamModal] = useState(false);
  const [upstreamData, setUpstreamData] = useState(null);
  const [upstreamLoading, setUpstreamLoading] = useState(false);
  const [upstreamError, setUpstreamError] = useState(null);
  // const { selectedResult, loadProgramById } = useAppContext(); // Removed context usage

  // Removed useEffect for fetching overview data as it's passed via props

  // Separate effect for fetching related programs data using programId
  useEffect(() => {
    const fetchRelatedPrograms = async () => {
      if (!programId) return; // Use programId prop

      setRelatedData(null); // Reset related data on new fetch
      setError(null); // Clear previous errors

      try {
        const related = await apiService.getRelated(programId); // Use programId prop
        setRelatedData(related || []); // Ensure it's an array
      } catch (err) {
        console.error('Failed to fetch related programs data:', err);
        // Not setting the main error state since this is non-critical
        setError('Failed to load related programs.'); // Set specific error
        setRelatedData([]);
      }
    };

    fetchRelatedPrograms();
  }, [programId]); // Dependency on programId

  // Helper function to get values for a specific key from mvinfo
  const getMvInfoValues = (key) => {
    if (!programOverview?.mvinfo) return []; // Use programOverview prop
    return programOverview.mvinfo
      .filter(item => item.key === key)
      .map(item => item.value);
  };

  // Format array of items to display as comma-separated string
  const formatArrayToString = (arr) => {
    if (!arr || arr.length === 0) return '-';
    return arr.join(', ');
  };

  // Format date to be more readable
  // Note: Date-only strings like "2012-06-20" are parsed as UTC midnight,
  // which can shift to the previous day when converted to local time.
  // We use UTC methods to avoid this timezone issue.
  const formatDate = (dateString) => {
    if (!dateString) return '-';
    try {
      const date = new Date(dateString);
      // Use UTC values to avoid timezone shifting for date-only strings
      return `${date.getUTCMonth() + 1}/${date.getUTCDate()}/${date.getUTCFullYear()}`;
    } catch (e) {
      return dateString;
    }
  };

  // Get the quality rating as a string
  const getQualityRating = () => {
    if (!programOverview?.qualityrating || programOverview.qualityrating.length === 0) return '-'; // Use programOverview prop
    const rating = programOverview.qualityrating[0];
    return `${rating.value}/5 (${rating.ratingsBody})`;
  };

  // Handle related program click
  const handleRelatedProgramClick = (tmsId) => {
    if (tmsId) {
      navigate(`/program/${tmsId}`); // Use navigate
    } else {
      console.error('Related program TMSId not found');
    }
  };

  // Handle parent program click
  const handleParentProgramClick = (tmsId) => {
    if (tmsId) {
      navigate(`/program/${tmsId}`); // Use navigate
    } else {
      console.error('Parent program ID not found');
    }
  };

  // Get the genres as a string
  const getGenresString = () => {
    if (!programOverview?.genres || programOverview.genres.length === 0) return '-'; // Use programOverview prop
    return programOverview.genres
      .sort((a, b) => a.genreOrd - b.genreOrd)
      .map(g => g.genre)
      .join(', ');
  };

  // Get the audio languages as a string
  const getAudioLanguages = () => {
    if (!programOverview?.origaudiolang || programOverview.origaudiolang.length === 0) return '-'; // Use programOverview prop
    // Convert language codes to full language names
    const languageMap = {
      'en': 'English',
      'es': 'Spanish',
      'fr': 'French',
      'de': 'German',
      'it': 'Italian',
      'ja': 'Japanese',
      'ko': 'Korean',
      'zh': 'Chinese',
      'ru': 'Russian'
      // Add more mappings as needed
    };

    return programOverview.origaudiolang
      .sort((a, b) => a.ord - b.ord)
      .map(lang => languageMap[lang.lang] || lang.lang)
      .join(', ');
  };

  // Render loading state (only needed if overview itself could be loading, which it isn't here anymore)
  // if (isLoading) { ... } // Removed overview loading state

  // Render error state (only for related programs fetch now)
  // if (error) { ... } // Error handling for related programs is shown inline below

  // Render empty state (if programOverview prop is missing)
  if (!programOverview) {
    return (
      <div className="w-full h-full p-4 flex justify-center items-center">
        <div className="text-gray-500 text-center">
          <p>{t('programSummary.noOverview')}</p>
        </div>
      </div>
    );
  }

  const seriesLikeInfoHeading = programOverview.progType === 'Podcast'
    ? t('programSummary.podcastInformation')
    : t('programSummary.seriesInformation');

  return (
    <div className="h-full overflow-auto p-6">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* General Information */}
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-100">
          <h3 className="text-lg font-medium text-gray-800 mb-4 text-center">{t('programSummary.generalInformation')}</h3>

          <div className="space-y-4">
            <div className="text-center">
              <h4 className="text-sm font-medium text-gray-500">{t('programSummary.genres')}</h4>
              <p className="text-sm mt-1">{getGenresString()}</p>
            </div>

            <div className="text-center">
              <h4 className="text-sm font-medium text-gray-500">{t('programSummary.programType')}</h4>
              <p className="text-sm mt-1">
                {programOverview.progType || '-'} {programOverview.subType ? `/ ${programOverview.subType}` : ''}
              </p>
            </div>

            <div className="text-center">
              <h4 className="text-sm font-medium text-gray-500">{t('programSummary.yearOfRelease')}</h4>
              <p className="text-sm mt-1">{formatArrayToString(getMvInfoValues('yearOfRelease'))}</p>
            </div>

            <div className="text-center">
              <h4 className="text-sm font-medium text-gray-500">{t('programSummary.originalAudioLanguage')}</h4>
              <p className="text-sm mt-1">{getAudioLanguages()}</p>
            </div>

            <div className="text-center">
              <h4 className="text-sm font-medium text-gray-500">{t('programSummary.runtime')}</h4>
              <p className="text-sm mt-1">{programOverview.runTime || programOverview.duration ? t('common.runtimeMins', { count: programOverview.runTime || programOverview.duration }) : '-'}</p>
            </div>

            <div className="text-center">
              <h4 className="text-sm font-medium text-gray-500">{t('programSummary.qualityRating')}</h4>
              <p className="text-sm mt-1">{getQualityRating()}</p>
            </div>
          </div>
        </div>

        {/* Production Details */}
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-100">
          <h3 className="text-lg font-medium text-gray-800 mb-4 text-center">{t('programSummary.productionDetails')}</h3>

          <div className="space-y-4">
            <div className="text-center">
              <h4 className="text-sm font-medium text-gray-500">{t('programSummary.productionCompanies')}</h4>
              <p className="text-sm mt-1">{formatArrayToString(getMvInfoValues('productionCompany'))}</p>
            </div>

            <div className="text-center">
              <h4 className="text-sm font-medium text-gray-500">{t('programSummary.pictureFormat')}</h4>
              <p className="text-sm mt-1">{formatArrayToString(getMvInfoValues('pictureFormat'))}</p>
            </div>

            <div className="text-center">
              <h4 className="text-sm font-medium text-gray-500">{t('programSummary.soundMix')}</h4>
              <p className="text-sm mt-1">{formatArrayToString(getMvInfoValues('soundMix'))}</p>
            </div>

            <div className="text-center">
              <h4 className="text-sm font-medium text-gray-500">{t('programSummary.color')}</h4>
              <p className="text-sm mt-1">{programOverview.colorCode || '-'}</p>
            </div>

            <div className="text-center">
              <h4 className="text-sm font-medium text-gray-500">{t('programSummary.animation')}</h4>
              <p className="text-sm mt-1">{programOverview.animation ? t('programSummary.yes') : t('programSummary.no')}</p>
            </div>
          </div>
        </div>

        {/* Series Information or Additional Information based on program type */}
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-100">
          <h3 className="text-lg font-medium text-gray-800 mb-4 text-center">
            {(programOverview.progType === 'Series' || programOverview.progType === 'Podcast') ? seriesLikeInfoHeading : t('programSummary.additionalInformation')}
          </h3>

          <div className="space-y-4">
            {(programOverview.progType === 'Series' || programOverview.progType === 'Podcast') ? (
              // Series-specific information
              <>
                <div className="text-center">
                  <h4 className="text-sm font-medium text-gray-500">{t('programSummary.originalAirDate')}</h4>
                  <p className="text-sm mt-1">{formatDate(programOverview.origAirDate)}</p>
                </div>

                <div className="text-center">
                  <h4 className="text-sm font-medium text-gray-500">{t('programSummary.seriesPremiere')}</h4>
                  <p className="text-sm mt-1">{formatDate(programOverview.seriesPremiere)}</p>
                </div>

                <div className="text-center">
                  <h4 className="text-sm font-medium text-gray-500">{t('programSummary.seriesFinale')}</h4>
                  <p className="text-sm mt-1">{formatDate(programOverview.seriesFinale)}</p>
                </div>

                <div className="text-center">
                  <h4 className="text-sm font-medium text-gray-500">{t('programSummary.originalNetwork')}</h4>
                  <p className="text-sm mt-1">{programOverview.origNetwork || '-'}</p>
                </div>
              </>
            ) : (
              // Default additional information for non-series
              <>
                {programOverview.trailers && programOverview.trailers.length > 0 ? (
                  <div className="text-center">
                    <h4 className="text-sm font-medium text-gray-500">{t('programSummary.trailers')}</h4>
                    <ul className="text-sm mt-1 space-y-1">
                      {programOverview.trailers.map((trailer, index) => (
                        <li key={index} className="overflow-hidden text-ellipsis">
                          <a
                            href={trailer.URL}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:underline"
                          >
                            {trailer.URL}
                          </a>
                        </li>
                      ))}
                    </ul>
                  </div>
                ) : (
                  <div className="text-center">
                    <h4 className="text-sm font-medium text-gray-500">{t('programSummary.trailers')}</h4>
                    <p className="text-sm mt-1">-</p>
                  </div>
                )}
              </>
            )}

            <div className="text-center">
              <h4 className="text-sm font-medium text-gray-500">{t('programSummary.tmsId')}</h4>
              <p className="text-sm mt-1">{programOverview.tmsId || programId || '-'}</p>
            </div>
          </div>
        </div>
      </div>

      {/* Parent Program Section */}
      {programOverview.connectorId && (programOverview.progType === 'Series' || programOverview.progType === 'Podcast') && programOverview.subType === 'Episode' && (
        <div className="mt-8">
          <h3 className="text-lg font-medium text-gray-800 mb-4">{t('programSummary.parentProgram')}</h3>

          <div className="grid grid-cols-1 sm:grid-cols-1 md:grid-cols-1 gap-4">
            <div className="border border-gray-200 rounded-md p-4 hover:bg-blue-50 transition-colors">
              <button
                onClick={() => handleParentProgramClick(programOverview.connectorId)}
                className="text-left w-full"
              >
                <h4 className="font-medium text-blue-600 mb-2">{programOverview.connectorTitle || t('programSummary.parentSeriesFallback')}</h4>
                <div className="text-xs text-gray-500 space-y-1">
                  <p><span className="font-medium">{t('programSummary.relationship')}</span> Parent</p>
                  <p><span className="font-medium">{t('programSummary.tmsId')}</span> {programOverview.connectorId}</p>
                  <p><span className="font-medium">{t('programSummary.language')}</span> {programOverview.lang === 'en' ? 'English' : programOverview.lang || t('programSummary.unknown')}</p>
                </div>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Related Programs Section - Conditionally Rendered */}
      {!(programOverview.progType === 'Sports event' && programOverview.subType === 'Sport') && (
        <div className="mt-8">
          <h3 className="text-lg font-medium text-gray-800 mb-4">{t('programSummary.relatedPrograms')}</h3>

          {relatedData === null ? ( // Check if relatedData is null (loading)
          // Loading state for related programs
          <div className="flex justify-center items-center p-6">
            <div className="flex flex-col items-center">
              <svg className="animate-spin h-6 w-6 text-blue-500 mb-2" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
              <span className="text-sm text-gray-600">{t('programSummary.loadingRelated')}</span>
            </div>
          </div>
        ) : error ? ( // Check for error after loading attempt
           <div className="text-center text-red-500 p-4 border border-red-200 rounded-md">
             {t('programSummary.failedLoadRelated')}
           </div>
        ) : relatedData.length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
            {relatedData.map((program, index) => (
              <div key={index} className="border border-gray-200 rounded-md p-4 hover:bg-blue-50 transition-colors">
                <button
                  onClick={() => handleRelatedProgramClick(program.TMSId)}
                  className="text-left w-full"
                >
                  <h4 className="font-medium text-blue-600 mb-2">{program.title}</h4>
                  <div className="text-xs text-gray-500 space-y-1">
                    <p><span className="font-medium">{t('programSummary.relationship')}</span> {program.relationship_type}</p>
                    <p><span className="font-medium">{t('programSummary.tmsId')}</span> {program.TMSId}</p>
                    <p><span className="font-medium">{t('programSummary.language')}</span> {program.lang === 'en' ? 'English' : program.lang}</p>
                  </div>
                </button>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center text-gray-500 p-4 border border-gray-200 rounded-md">
            {t('programSummary.noRelated')}
          </div>
          )}
        </div>
      )}

      {/* Upstream Data - only visible with program.search permission */}
      {hasPermission('program.search') && (
        <div className="mt-8 flex justify-center">
          <button
            onClick={async () => {
              setShowUpstreamModal(true);
              setUpstreamData(null);
              setUpstreamError(null);
              setUpstreamLoading(true);
              try {
                const data = await apiService.getUpstreamPrograms(programId);
                setUpstreamData(data);
              } catch (err) {
                console.error('Failed to fetch upstream data:', err);
                setUpstreamError(err);
              } finally {
                setUpstreamLoading(false);
              }
            }}
            className="text-gray-400 hover:text-blue-500 transition-colors"
            title={t('programSummary.upstreamData')}
          >
            <Cloud size={20} />
          </button>
        </div>
      )}

      <UpstreamDataModal
        isOpen={showUpstreamModal}
        onClose={() => setShowUpstreamModal(false)}
        data={upstreamData}
        isLoading={upstreamLoading}
        error={upstreamError}
      />
    </div>
  );
};

export default ProgramSummary;
